import { useEffect, useRef, useState } from 'react'

import { useLocation } from 'react-router-dom'
import i18n from 'src/i18n'

import {
  clearSignInPending,
  consumeSignInPending,
  setSignInPending,
} from 'src/app/auth/adapters/oauth-redirect/sign-in-pending-store'
import type { OAuthProvider } from 'src/app/auth/domain/models/oauth-provider'
import { useOAuthRedirect } from 'src/app/auth/repositories/use-oauth-redirect'
import { useToast } from 'src/app/core/hooks/use-toast'

interface ILoginLocationState {
  from?: { pathname?: string }
  sessionExpired?: boolean
}

export interface IUseLoginRedirectReturn {
  redirectTo: (provider: OAuthProvider) => void
  redirectingProvider?: OAuthProvider
}

export const useLoginRedirect = (): IUseLoginRedirectReturn => {
  const location = useLocation()
  const oauthRedirect = useOAuthRedirect()
  const { notify, toastType } = useToast()
  const [redirectingProvider, setRedirectingProvider] = useState<
    OAuthProvider | undefined
  >(undefined)
  const redirectingProviderRef = useRef<OAuthProvider | undefined>(undefined)

  useEffect(() => {
    redirectingProviderRef.current = redirectingProvider
  }, [redirectingProvider])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return
      if (!redirectingProviderRef.current) return
      redirectingProviderRef.current = undefined
      setRedirectingProvider(undefined)
      clearSignInPending()
      notify({
        type: toastType.WARNING,
        title: i18n.t('auth.login.errors.signInCancelled.title'),
        message: i18n.t('auth.login.errors.signInCancelled.message'),
      })
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [notify, toastType])

  useEffect(() => {
    if (!consumeSignInPending()) return
    notify({
      type: toastType.WARNING,
      title: i18n.t('auth.login.errors.signInCancelled.title'),
      message: i18n.t('auth.login.errors.signInCancelled.message'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const state = location.state as ILoginLocationState | null
    if (!state?.sessionExpired) return
    notify({
      type: toastType.WARNING,
      title: i18n.t('auth.login.sessionExpired.title'),
      message: i18n.t('auth.login.sessionExpired.message'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const redirectTo = (provider: OAuthProvider): void => {
    if (redirectingProviderRef.current) return
    const fromPath = (location.state as ILoginLocationState | null)?.from
      ?.pathname
    redirectingProviderRef.current = provider
    setRedirectingProvider(provider)
    setSignInPending()
    oauthRedirect.redirectTo({ provider, postLoginRedirect: fromPath })
  }

  return {
    redirectTo,
    redirectingProvider,
  }
}
